<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, isOpen ? 'device-active--access' : '']"
      :style="{ '--pin-active-color': 'var(--device-color-access)' }"
      :title="device.deviceName">
      <ControlStatusRing :value="percent" accent-color="var(--device-color-access)">
        <ControlIcon
          :name="isOpen ? 'door_open' : 'garage'"
          :size="pinIconSize"
          :style="isOpen ? { color: 'var(--device-color-access)' } : {}" />
      </ControlStatusRing>
      <div v-if="isOpen" class="device-icon-pin__glow"></div>
    </div>
  </template>

  <!-- ── Grid mode ─────────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="isOpen ? 'door_open' : 'garage'"
      :name="displayName"
      :is-on="isOpen"
      active-color="orange-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <!-- The sheet is the door's own: the state, how far it is open, and the
       three verbs a motor is driven with (`open` / `stop` / `close`, written
       in the spelling the device's own `command` row carries — see
       `utils/garageDoorState.ts`). A door is NOT a cover: the hub's class
       declares `position` read-only and `command` as the control, so there is
       no drag-the-percentage here — the chips are the control. -->
  <template v-else-if="mode === 'control'">
    <div class="garage-sheet">
      <div class="garage-hero">
        <button
          type="button"
          class="garage-circle"
          :class="{ 'garage-circle--open': isOpen, 'garage-circle--busy': controlling }"
          :disabled="controlling || percent === null"
          :title="isOpen ? tt('Close the door', 'Zapri vrata') : tt('Open the door', 'Odpri vrata')"
          @click="handleToggle">
          <ControlIcon :name="isOpen ? 'door_open' : 'garage'" size="2.5rem" />
        </button>
        <div class="garage-state" :class="{ 'garage-state--open': isOpen, 'garage-state--moving': state === 'moving' }">
          {{ stateWord }}
        </div>
        <div class="garage-caption">{{ caption }}</div>
      </div>

      <!-- How far it is open, as a rail — the reading, not a control. -->
      <div class="garage-rail" role="img" :aria-label="caption">
        <span
          class="garage-rail__fill"
          :class="{ 'garage-rail__fill--moving': state === 'moving' }"
          :style="{ width: `${percent ?? 0}%` }" />
      </div>

      <div class="garage-actions">
        <ControlChip
          :disabled="controlling || !hasCommand"
          :active="state === 'open'"
          accent="var(--device-color-access)"
          @click="handleAction('open')">
          {{ tt(GARAGE_DOOR_ACTION_LABELS.open.en, GARAGE_DOOR_ACTION_LABELS.open.sl) }}
        </ControlChip>
        <ControlChip
          :disabled="controlling || !hasCommand || state !== 'moving'"
          accent="var(--device-color-access)"
          @click="handleAction('stop')">
          {{ tt(GARAGE_DOOR_ACTION_LABELS.stop.en, GARAGE_DOOR_ACTION_LABELS.stop.sl) }}
        </ControlChip>
        <ControlChip
          :disabled="controlling || !hasCommand"
          :active="state === 'closed'"
          accent="var(--device-color-access)"
          @click="handleAction('close')">
          {{ tt(GARAGE_DOOR_ACTION_LABELS.close.en, GARAGE_DOOR_ACTION_LABELS.close.sl) }}
        </ControlChip>
      </div>

      <p v-if="autoCloseLabel" class="garage-note">{{ autoCloseLabel }}</p>
      <p v-if="safetyLabel" class="garage-note">{{ safetyLabel }}</p>
      <p v-if="!hasCommand" class="garage-note garage-note--warn">
        {{
          tt(
            'This door reports no command — it can be watched, not driven.',
            'Ta vrata ne poročajo ukaza — lahko jih spremljate, ne upravljate.',
          )
        }}
      </p>

      <p v-if="lastUpdated" class="garage-updated">
        {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
      </p>

      <SetpointTimeline
        :device-values="device.deviceValues ?? []"
        :device-id="device.id"
        accent-color="rgb(251, 146, 60)" />
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import SetpointTimeline from './SetpointTimeline.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import { tt } from '../../utils/consoleText';
import {
  GARAGE_DOOR_ACTION_LABELS,
  GARAGE_DOOR_COMMAND_KEY,
  GARAGE_DOOR_STATE_LABELS,
  garageDoorCommandValue,
  garageDoorPercent,
  garageDoorState,
  isGarageDoorOpen,
  type GarageDoorAction,
} from '../../utils/garageDoorState';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  /** Layout-aware (the dashboard hands these to light/cover/climate too). */
  colSpan?: number;
  rowSpan?: number;
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

/** How far the door is open (0 = closed, 100 = fully open), or null. */
const percent = computed(() => garageDoorPercent(props.device.deviceValues, currentValues.value));
const state = computed(() => garageDoorState(percent.value));
const isOpen = computed(() => isGarageDoorOpen(percent.value));

const stateWord = computed(() => {
  const label = GARAGE_DOOR_STATE_LABELS[state.value];
  return tt(label.en, label.sl);
});

const caption = computed(() => {
  if (percent.value === null) {
    return tt('This door reports no position.', 'Ta vrata ne poročajo položaja.');
  }
  return tt(`${percent.value}% open`, `${percent.value}% odprta`);
});

/** The device's own `command` row — what a chip writes through. */
const hasCommand = computed(() =>
  (props.device.deviceValues ?? []).some((row) => String(row.valueKey ?? '') === GARAGE_DOOR_COMMAND_KEY),
);

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

/** The class' own settings, read defensively (they arrive as an untyped bag). */
function settingNumber(key: string): number | null {
  const settings = (props.device as { settings?: unknown }).settings;
  if (settings === null || typeof settings !== 'object') return null;
  const value = (settings as Record<string, unknown>)[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

const autoCloseLabel = computed(() => {
  const delay = settingNumber('auto_close_delay');
  if (delay === null || delay <= 0) return '';
  return tt(`Auto-closes after ${delay} s`, `Samodejno zapre po ${delay} s`);
});

const safetyLabel = computed(() => {
  const timeout = settingNumber('safety_timeout');
  if (timeout === null || timeout <= 0) return '';
  return tt(`Stops itself after ${timeout} s in motion`, `Ustavi se po ${timeout} s gibanja`);
});

// ── Writes ───────────────────────────────────────────────────────────────
async function write(action: GarageDoorAction): Promise<void> {
  const ok = await setProperty(
    props.device,
    GARAGE_DOOR_COMMAND_KEY,
    garageDoorCommandValue(props.device.deviceValues, action),
  );
  if (ok) emit('value-update', props.device.id, action);
}

/** The hero circle toggles: open when closed, close when open. */
async function handleToggle(): Promise<void> {
  if (controlling.value || percent.value === null) return;
  await write(isOpen.value ? 'close' : 'open');
}

async function handleAction(action: GarageDoorAction): Promise<void> {
  if (controlling.value || !hasCommand.value) return;
  // A chip for the state the door is already in is a no-op.
  if (action === 'open' && state.value === 'open') return;
  if (action === 'close' && state.value === 'closed') return;
  await write(action);
}

// ── Display ──────────────────────────────────────────────────────────────
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

// colSpan/rowSpan are accepted so the dashboard can hand them like it does to
// the other layout-aware surfaces; the sheet itself is fluid.
void props.colSpan;
void props.rowSpan;
</script>

<style scoped>
.garage-sheet {
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  padding: 0.25em 0;
}

.garage-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35em;
}

.garage-circle {
  width: 5.5em;
  height: 5.5em;
  border-radius: 9999px;
  border: 2px solid var(--color-border, rgba(148, 163, 184, 0.4));
  background: var(--color-surface, rgba(148, 163, 184, 0.08));
  color: var(--color-text, inherit);
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.18s ease,
    background 0.18s ease,
    color 0.18s ease;
}

.garage-circle--open {
  border-color: var(--device-color-access);
  color: var(--device-color-access);
  background: color-mix(in srgb, var(--device-color-access) 14%, transparent);
}

.garage-circle--busy {
  opacity: 0.6;
}

.garage-state {
  font-size: 1.05em;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.garage-state--open {
  color: var(--device-color-access);
}

.garage-state--moving {
  color: var(--color-text-muted, inherit);
  opacity: 0.85;
}

.garage-caption {
  font-size: 0.78em;
  opacity: 0.7;
}

.garage-rail {
  position: relative;
  height: 0.45em;
  border-radius: 9999px;
  background: color-mix(in srgb, currentColor 12%, transparent);
  overflow: hidden;
}

.garage-rail__fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--device-color-access);
  transition: width 0.25s ease;
}

.garage-rail__fill--moving {
  background: repeating-linear-gradient(
    45deg,
    var(--device-color-access) 0 8px,
    color-mix(in srgb, var(--device-color-access) 55%, transparent) 8px 16px
  );
}

.garage-actions {
  display: flex;
  gap: 0.4em;
  flex-wrap: wrap;
}

.garage-note {
  font-size: 0.72em;
  opacity: 0.6;
  margin: 0;
}

.garage-note--warn {
  color: rgb(251, 146, 60);
  opacity: 0.9;
}

.garage-updated {
  font-size: 0.7em;
  opacity: 0.55;
  margin: 0;
}
</style>
